export const countryPreviewContent = {
  portugal: {
    slug: "portugal",
    name: "Portugal",
    code: "PT",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Portugal matters in the Relocation Clarity model because it can sit at a useful intersection of residency relevance, healthcare confidence, coastal lifestyle appeal, and day-to-day practicality — with important regional differences that deserve early attention.",
    heroHighlights: [
      "Residency relevance",
      "Coastal lifestyle appeal",
      "Healthcare confidence",
      "Regional variety",
    ],
    images: [
      {
        url: "https://images.unsplash.com/photo-1735836219473-f31cd1fb6921?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxMaXNib24lMjBQb3J0dWdhbCUyMGNpdHlzY2FwZXxlbnwwfHx8fDE3NzQ3MzA2MjJ8MA&ixlib=rb-4.1.0&q=85",
        alt: "Lisbon city view with terracotta rooftops",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/35161321/pexels-photo-35161321.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Portugal coastal cliffs and Atlantic water",
        label: "Coastal lifestyle",
      },
      {
        url: "https://images.unsplash.com/photo-1742087710299-3c74add6d5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxQb3J0dWdhbCUyMG5laWdoYm9yaG9vZCUyMHN0cmVldHxlbnwwfHx8fDE3NzQ3MzA2MjV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Quiet Portugal neighborhood street with pastel buildings",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1769304681233-417f94334310?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxQb3J0dWdhbCUyMHJlZ2lvbmFsJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3NDczMDYyN3ww&ixlib=rb-4.1.0&q=85",
        alt: "Regional Portugal landscape with rural road and stone tower",
        label: "Regional variety",
      },
    ],
    whyIncluded: [
      "Portugal stays relevant because it consistently combines real long-stay pathway relevance with stronger healthcare confidence, workable daily infrastructure, and broad lifestyle appeal.",
      "It also offers meaningful range: some users look at Portugal for coastal living, safety, and expat familiarity, while others value it as a more balanced Europe option — provided region choice is handled carefully.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Still offers relative value versus many Western European peers, but Lisbon, Porto, and the Algarve can feel much tighter than the Portugal stereotype suggests.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Public and private care together give Portugal a stronger healthcare case than many lifestyle-first alternatives.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Common pathways exist, but the process can feel slower and more administrative than the headline narrative implies.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate to Strong",
        detail: "English goes further in major cities and expat-heavy areas than it does in smaller everyday settings.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Good internet reliability and workable daily infrastructure support remote work better than many lower-cost peers.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Portugal is especially relevant for users who value a milder climate, coastal access, and a calmer day-to-day rhythm.",
      },
    ],
    bestFit: [
      "Lower living costs than many Western European alternatives",
      "Healthcare quality with a stronger systems baseline",
      "Remote work practicality and dependable internet",
      "Coastal or outdoor lifestyle orientation",
      "Safety, steadiness, and slower pace of life",
      "Established expat communities in selected regions",
    ],
    tradeoffs: [
      "Administrative processes can feel slower and more bureaucratic than the move-first narrative suggests.",
      "English is workable in many urban and expat-heavy pockets, but local language matters more once you move beyond those bubbles.",
      "In Lisbon, Porto, and the Algarve, your housing choices will matter more here and can change the affordability picture quickly.",
      "Portugal is not one uniform experience: region choice changes cost, pace, access, and daily fit more than many users expect.",
      "Local job-market options for foreigners are narrower than the country’s lifestyle reputation can imply, especially without language depth.",
    ],
    regions: [
      {
        name: "Lisbon",
        note: "Best if you want the strongest international connectivity, urban energy, and service access — and you can accept that your housing choices will matter more here.",
      },
      {
        name: "Porto",
        note: "A smaller-scale urban base that often feels more grounded than Lisbon while still offering strong walkability and infrastructure.",
      },
      {
        name: "Algarve",
        note: "Most relevant for coastal lifestyle seekers who value weather and expat familiarity, but not always for budget relief.",
      },
      {
        name: "Silver Coast",
        note: "A useful middle option if you want ocean access and a calmer rhythm without defaulting straight to Lisbon or the Algarve.",
      },
      {
        name: "Central Portugal",
        note: "Worth exploring for lower-cost, lower-density living — especially if you can tolerate slower pace and more car dependence.",
      },
    ],
    cta: {
      eyebrow: "See Portugal through your own priorities",
      title: "Use the assessment to find out whether Portugal is actually one of your strongest fits.",
      description:
        "This preview is only the orientation layer. The stronger value comes from seeing Portugal in context — against your budget, timeline, tradeoffs, and realistic alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  spain: {
    slug: "spain",
    name: "Spain",
    code: "ES",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Spain matters in the Relocation Clarity model because it often combines healthcare strength, strong infrastructure, lifestyle range, and regional flexibility — but with sharper cost and language tradeoffs than some users expect.",
    heroHighlights: ["Healthcare strength", "Regional variety", "Urban-to-coastal range", "European base"],
    images: [
      {
        url: "https://images.pexels.com/photos/4103277/pexels-photo-4103277.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Aerial view of Alicante's urban coastline",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/10801110/pexels-photo-10801110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Barcelona coastline and city skyline",
        label: "Coastal living",
      },
      {
        url: "https://images.unsplash.com/photo-1717687016843-ba554d1e5d1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxNYWRyaWQlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM0NDI3fDA&ixlib=rb-4.1.0&q=85",
        alt: "Madrid neighborhood street with mural and apartments",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/19704676/pexels-photo-19704676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Garachico coastline and town in Spain",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Spain stays in the model because it can offer a compelling mix of healthcare confidence, everyday livability, and strong infrastructure for people who want a more established European base rather than a purely low-cost move.",
      "It also has unusually broad internal range: some users are evaluating Spain for major-city energy and connectivity, while others are drawn to warmer coastal regions, slower regional living, or a stronger all-around quality-of-life balance.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Still more workable than some premium European peers, but Madrid, Barcelona, and high-demand coastal zones can narrow the value case quickly.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Spain remains relevant partly because healthcare confidence is one of its clearest strengths in the European shortlist.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "There are realistic long-stay pathways, though documentation and timing can feel more administrative than simple in practice.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English is more workable in international pockets than in everyday local settings, especially outside major hubs.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Spain generally performs well on daily infrastructure, transport, and internet reliability, which helps remote practicality.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Climate, food culture, street life, and regional variety are part of why Spain keeps appearing in real relocation comparisons.",
      },
    ],
    bestFit: [
      "Healthcare quality and stronger systems confidence",
      "A more established European living base",
      "Urban and coastal choice within one country",
      "Remote work practicality with good infrastructure",
      "Warmer climate with strong day-to-day livability",
      "Regional flexibility rather than one default lifestyle",
    ],
    tradeoffs: [
      "In bigger cities and top coastal zones, your housing choices will matter more here and can change the affordability equation quickly.",
      "Spanish matters more in daily life than some new arrivals expect, especially outside international neighborhoods.",
      "Administrative timing can feel slower or more document-heavy than the lifestyle narrative suggests.",
      "Spain is not one uniform fit: Madrid, Barcelona, Valencia, Andalusia, and island/coastal areas can feel very different in pace and cost.",
      "For some users, formal local-employment routes will feel less straightforward than the overall lifestyle appeal implies.",
    ],
    regions: [
      {
        name: "Madrid",
        note: "Best if you want scale, connectivity, and stronger professional infrastructure more than coastal living.",
      },
      {
        name: "Barcelona",
        note: "Relevant for users who want international energy and urban-coastal access, while accepting sharper price pressure.",
      },
      {
        name: "Valencia",
        note: "Often worth exploring as a more balanced option between major-city livability, coast, and cost.",
      },
      {
        name: "Costa del Sol / Málaga",
        note: "Most relevant for climate-first or lifestyle-first users who still want service access and strong expat familiarity.",
      },
      {
        name: "Seville / Inland Andalusia",
        note: "Useful if you want culture and slower rhythm, but climate intensity and local-language dependence matter more here.",
      },
    ],
    cta: {
      eyebrow: "See Spain through your own priorities",
      title: "Use the assessment to see whether Spain is a strong fit — or only a strong idea.",
      description:
        "This preview is the orientation layer only. The deeper value comes from seeing Spain against your budget, timeline, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  mexico: {
    slug: "mexico",
    name: "Mexico",
    code: "MX",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Mexico matters in the Relocation Clarity model because it often offers strong geographic practicality, broad regional choice, and cost flexibility — but with bigger local variation in infrastructure, security, and day-to-day fit than a single headline can capture.",
    heroHighlights: ["Regional variation", "Time-zone practicality", "Cost flexibility", "North America proximity"],
    images: [
      {
        url: "https://images.pexels.com/photos/12162896/pexels-photo-12162896.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Mexico City skyline at twilight",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1772048208046-d8a8bd2cd042?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxNZXhpY28lMjBDaXR5JTIwc3RyZWV0JTIwbmVpZ2hib3Job29kfGVufDB8fHx8MTc3NDczNDQxNHww&ixlib=rb-4.1.0&q=85",
        alt: "Tree-lined street in Mexico City neighborhood",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1772657340632-0d147e8224d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxNZXhpY28lMjBjb2FzdGxpbmUlMjB0b3dufGVufDB8fHx8MTc3NDczNDQxN3ww&ixlib=rb-4.1.0&q=85",
        alt: "Mexico beach with buildings at dusk",
        label: "Coastal living",
      },
      {
        url: "https://images.pexels.com/photos/19749394/pexels-photo-19749394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Río Lagartos harbor in Mexico",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Mexico stays relevant because it can be unusually practical for users who care about cost flexibility, easier time-zone alignment with North American work, and a wide range of lifestyle bases without leaving the region entirely.",
      "It also forces better decision discipline: Mexico can work very well for the right user, but the regional differences in security, healthcare access, infrastructure, climate, and pace are significant enough that broad assumptions can mislead people early.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Mexico can offer meaningful cost relief, though value depends heavily on city, neighborhood, and expat demand pressure.",
      },
      {
        label: "Healthcare",
        signal: "Balanced to Strong",
        detail: "Healthcare can be very workable in major-city and well-established private-care corridors, but consistency is not uniform nationwide.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Residency relevance is part of Mexico’s appeal, especially for users prioritizing a more accessible regional move.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English is easier in some expat and tourism-oriented pockets than it is in everyday local life.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "Internet and daily infrastructure can be strong in the right places, but this is much more location-sensitive than in some other shortlist countries.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Mexico remains relevant partly because it offers real range: dense urban life, colonial-city living, coast, mountains, and lower-intensity regional options.",
      },
    ],
    bestFit: [
      "Closer geographic and time-zone alignment with North America",
      "Meaningful cost flexibility with regional choice",
      "Warmer climate or outdoor-oriented living",
      "A move that feels less culturally distant than many overseas options",
      "Established expat communities in specific hubs",
      "A country where neighborhood and city choice can tailor the experience",
    ],
    tradeoffs: [
      "Regional differences in safety and day-to-day predictability matter more here than in some higher-system-consistency countries.",
      "Healthcare, infrastructure, and local administration can feel highly place-dependent rather than nationally even.",
      "English is useful in some pockets, but Spanish matters much more once you move beyond expat-oriented zones.",
      "Some coastal and expat-heavy markets lose their value edge faster than newcomers expect.",
      "Mexico rewards city-level and neighborhood-level judgment more than broad country-level assumptions.",
    ],
    regions: [
      {
        name: "Mexico City",
        note: "Best if you want scale, culture, private healthcare access, and stronger urban infrastructure more than a slower pace.",
      },
      {
        name: "Mérida",
        note: "Often explored for steadier pace, relative safety perception, and warmer lifestyle, with less big-city energy.",
      },
      {
        name: "Lake Chapala / Ajijic",
        note: "Most relevant for users who want a well-established expat environment and a softer landing into local life.",
      },
      {
        name: "Puerto Vallarta",
        note: "Useful for coastal lifestyle and expat familiarity, though your housing choices will matter more here and can weaken the value case.",
      },
      {
        name: "Oaxaca",
        note: "Worth exploring if cultural depth and slower rhythm matter more than maximum infrastructure or international convenience.",
      },
    ],
    cta: {
      eyebrow: "See Mexico through your own priorities",
      title: "Use the assessment to see whether Mexico fits your real constraints — not just the headline appeal.",
      description:
        "This preview is only the orientation layer. The stronger value comes from understanding which part of Mexico fits you, and how it compares with your alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  "costa-rica": {
    slug: "costa-rica",
    name: "Costa Rica",
    code: "CR",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Costa Rica matters in the Relocation Clarity model because it often appeals to users looking for political steadiness, nature-led lifestyle, and an easier emotional transition into Latin America — while requiring more realism on cost and infrastructure than its image sometimes suggests.",
    heroHighlights: ["Nature-led lifestyle", "Regional steadiness", "Residency relevance", "Expat familiarity"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1666606654535-cc987a43f814?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxTYW4lMjBKb3NlJTIwQ29zdGElMjBSaWNhJTIwY2l0eSUyMHN0cmVldHxlbnwwfHx8fDE3NzQ3MzQ0MzF8MA&ixlib=rb-4.1.0&q=85",
        alt: "People walking on a San José street",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/11181956/pexels-photo-11181956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Aerial view of Jaco Beach town in Costa Rica",
        label: "Coastal living",
      },
      {
        url: "https://images.unsplash.com/photo-1666606654465-675d553fd218?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxTYW4lMjBKb3NlJTIwQ29zdGElMjBSaWNhJTIwY2l0eSUyMHN0cmVldHxlbnwwfHx8fDE3NzQ3MzQ0MzF8MA&ixlib=rb-4.1.0&q=85",
        alt: "Street scene in San José, Costa Rica",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/8592270/pexels-photo-8592270.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Costa Rica central valley landscape at sunset",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Costa Rica stays relevant because it can offer a steadier-feeling Latin American base for users who prioritize nature, climate, and a slower day-to-day rhythm without wanting a move that feels overly hard to navigate emotionally.",
      "It is also useful in the model because it is not automatically the value option people imagine: Costa Rica can work well, but the real decision often depends on whether lifestyle quality and relative stability matter more than lower-cost alternatives elsewhere.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Costa Rica is often more expensive than newcomers expect, especially compared with other Latin American lifestyle contenders.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare is part of Costa Rica’s relevance, particularly for users comparing lifestyle-focused countries that still need a workable systems baseline.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Long-stay relevance is real here, though the administrative side still needs more patience than the relaxed-country image suggests.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can be workable in expat-heavy areas, but everyday integration still improves significantly with Spanish.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate",
        detail: "Costa Rica can be very livable, but infrastructure and transport convenience are not as uniformly smooth as in some higher-system countries.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "For users drawn to greenery, weather, wildlife, and lower-intensity daily rhythm, Costa Rica remains one of the clearest lifestyle-led options.",
      },
    ],
    bestFit: [
      "Nature-led or outdoor-first day-to-day living",
      "A slower pace with strong lifestyle emphasis",
      "Relative regional stability within Latin America",
      "Residency options that feel worth exploring early",
      "Expat familiarity in selected regions",
      "Users prioritizing environment and emotional quality of life over maximum cost efficiency",
    ],
    tradeoffs: [
      "Costa Rica is often not the bargain many people assume once housing, transport, and imported comforts enter the picture.",
      "Roads, transport logistics, and some local systems can feel slower or less frictionless than the lifestyle image suggests.",
      "Regional choice matters: beach towns, Central Valley areas, and quieter inland communities can feel very different in practicality.",
      "English helps in some expat-heavy areas, but Spanish still matters for smoother local navigation and longer-term integration.",
      "For some users, the market feels smaller and less urbanly dynamic than Mexico or larger international hubs.",
    ],
    regions: [
      {
        name: "Central Valley",
        note: "Usually the first place to explore if healthcare access, schools, and day-to-day practicality matter more than beach living.",
      },
      {
        name: "Escazú / Santa Ana",
        note: "Useful for users who want a more polished suburban-urban setup with stronger expat convenience and service access.",
      },
      {
        name: "Guanacaste",
        note: "Best for drier climate and beach-oriented living, with a more lifestyle-first feel and less urban depth.",
      },
      {
        name: "Nosara / Nicoya Coast",
        note: "Most relevant for wellness or remote-lifestyle seekers, though housing and convenience tradeoffs can be sharper here.",
      },
      {
        name: "Southern Zone / Uvita",
        note: "Worth exploring if you want greener, quieter, lower-density coastal living and can accept more logistical distance.",
      },
    ],
    cta: {
      eyebrow: "See Costa Rica through your own priorities",
      title: "Use the assessment to see whether Costa Rica is a true fit — not just a strong lifestyle image.",
      description:
        "This preview is the orientation layer only. The deeper value comes from seeing where Costa Rica fits against your budget, pace, and alternative shortlist countries.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  thailand: {
    slug: "thailand",
    name: "Thailand",
    code: "TH",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Thailand matters in the Relocation Clarity model because it often combines strong affordability, surprisingly capable urban infrastructure, and highly livable daily convenience — while still requiring more cultural, legal, and residency realism than some users budget for.",
    heroHighlights: ["Affordability", "Urban convenience", "Private healthcare strength", "Foreignness tradeoff"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1694533647295-adeb1addada1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxUaGFpbGFuZCUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzQ0MDR8MA&ixlib=rb-4.1.0&q=85",
        alt: "Thailand city skyline viewed from above",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/5185616/pexels-photo-5185616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Tropical beach town in Thailand from above",
        label: "Coastal living",
      },
      {
        url: "https://images.pexels.com/photos/33636735/pexels-photo-33636735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Thailand street market at sunset",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1740745137865-8e26ae3ea020?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxUaGFpbGFuZCUyMGJlYWNoJTIwdG93bnxlbnwwfHx8fDE3NzQ3MzQ0OTh8MA&ixlib=rb-4.1.0&q=85",
        alt: "Thailand beach and mountain coastline",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Thailand stays relevant because it can offer one of the strongest day-to-day value propositions in the model for users who want lower costs, high convenience, and private-sector healthcare or urban infrastructure that feels more capable than expected.",
      "It also stays in the model because it is not a simple lifestyle pick: Thailand can work exceptionally well for the right user, but the residency setup, cultural distance, legal texture, and language gap make it a very different proposition from Mediterranean or Latin American options.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Thailand remains one of the more compelling value plays in the shortlist, especially for users comparing urban convenience against cost.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Private healthcare is part of why Thailand stays decision-relevant, particularly in the better-connected cities.",
      },
      {
        label: "Residency Accessibility",
        signal: "Mixed",
        detail: "Long-stay possibilities exist, but the pathway picture can feel more patchwork and policy-sensitive than some other options.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can be workable in international or tourist-heavy environments, but everyday local integration still carries a real language gap.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "In the right hubs, internet, transport, and service convenience can be better than many people expect at the price point.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Warm weather, food culture, convenience, and urban-energy-versus-island choice all contribute to Thailand’s staying power in comparisons.",
      },
    ],
    bestFit: [
      "Lower-cost living with strong daily convenience",
      "Private healthcare and service access in key hubs",
      "Remote work or location-flexible living",
      "Warm climate and food-led day-to-day life",
      "Users comfortable with stronger cultural difference from home",
      "A move where urban efficiency matters as much as lifestyle appeal",
    ],
    tradeoffs: [
      "Residency pathways can feel less linear or more policy-sensitive than users expect from the outside.",
      "Language and cultural distance are real, especially if you want life to extend meaningfully beyond expat or tourism layers.",
      "Air quality, heat, and seasonal conditions can materially affect day-to-day fit depending on region.",
      "Thailand can be very convenient, but legal and social texture may still feel more foreign or less intuitive than European or regional options.",
      "City, island, and northern living all solve different problems — they should not be treated as one Thailand experience.",
    ],
    regions: [
      {
        name: "Bangkok",
        note: "Best if you want maximum infrastructure, healthcare access, transport, and urban convenience at scale.",
      },
      {
        name: "Chiang Mai",
        note: "Often explored for lower-cost city living and remote-work practicality, with a calmer rhythm than Bangkok.",
      },
      {
        name: "Phuket",
        note: "Relevant for users prioritizing coast and service access, but it usually carries more expat and pricing distortion.",
      },
      {
        name: "Hua Hin",
        note: "A quieter option worth exploring if you want a more manageable coastal base without defaulting to an island setting.",
      },
      {
        name: "Koh Samui / Southern Islands",
        note: "Most relevant for lifestyle-led users who can accept greater distance from major-city systems and services.",
      },
    ],
    cta: {
      eyebrow: "See Thailand through your own priorities",
      title: "Use the assessment to see whether Thailand is a true operational fit — not just a strong value signal.",
      description:
        "This preview is only the orientation layer. The deeper value comes from seeing how Thailand compares against your timeline, tolerance for adaptation, and shortlist alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  malaysia: {
    slug: "malaysia",
    name: "Malaysia",
    code: "MY",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Malaysia matters in the Relocation Clarity model because it can offer one of the calmer value-plus-infrastructure propositions in Asia — often pairing cost efficiency, healthcare access, English usability, and regional practicality more smoothly than users expect.",
    heroHighlights: ["Value plus infrastructure", "English usability", "Regional hub practicality", "Urban livability"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1571490989267-7b828c10b5ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxNYWxheXNpYSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzQ0MDZ8MA&ixlib=rb-4.1.0&q=85",
        alt: "Kuala Lumpur skyline",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/7456602/pexels-photo-7456602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Malaysia coastal cityscape in Terengganu",
        label: "Coastal living",
      },
      {
        url: "https://images.pexels.com/photos/6770742/pexels-photo-6770742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Historic Penang-style street scene",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1608982398895-28ae86b19ae4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxNYWxheXNpYSUyMEt1YWxhJTIwTHVtcHVyJTIwc3RyZWV0JTIwc2t5bGluZXxlbnwwfHx8fDE3NzQ3MzQ0MzV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Street in Malaysia with skyline in the distance",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Malaysia stays relevant because it can give users a rare combination of affordability, healthcare confidence, English usability, and decent everyday infrastructure without demanding the same level of adaptation some neighboring options require.",
      "It also matters because its strengths are more practical than flashy: for the right person, Malaysia can feel less romanticized than Thailand but more stable and operationally workable as an actual base rather than a short-phase experiment.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Malaysia often performs well when users want cost efficiency without dropping too far in comfort or infrastructure.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare access is one of Malaysia’s clearest practical strengths in the broader Asia shortlist.",
      },
      {
        label: "Residency Accessibility",
        signal: "Mixed to Practical",
        detail: "Long-stay relevance exists, though program details and policy texture should be treated carefully rather than assumed stable.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "English usability is part of Malaysia’s appeal, especially compared with other lower-cost Asian options.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Urban infrastructure and internet reliability help Malaysia feel more operationally smooth than its price point might suggest.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Malaysia tends to appeal more as a practical, comfortable base than as a pure lifestyle fantasy destination — which is part of its value.",
      },
    ],
    bestFit: [
      "Value without sacrificing too much comfort or infrastructure",
      "English usability in daily life",
      "Healthcare access and calmer operational practicality",
      "Remote or flexible work with good connectivity",
      "A regional Asian base with easier travel access",
      "Users who prefer practical livability over a more romanticized relocation narrative",
    ],
    tradeoffs: [
      "Residency assumptions should be checked carefully because program rules and policy framing can shift over time.",
      "Heat, humidity, and tropical climate wear are real factors in daily fit.",
      "Some areas feel more car-dependent or less naturally walkable than new arrivals expect.",
      "Malaysia’s social and legal texture can feel more conservative than some users are looking for.",
      "The country can be extremely practical, but it may feel less culturally intuitive or emotionally immediate than a closer-to-home option.",
    ],
    regions: [
      {
        name: "Kuala Lumpur",
        note: "Best if you want maximum healthcare access, infrastructure, and urban practicality in one place.",
      },
      {
        name: "Penang",
        note: "Often the first region to explore if you want a more lifestyle-friendly urban base with strong food culture and expat familiarity.",
      },
      {
        name: "Johor Bahru",
        note: "Relevant for users who care about Singapore adjacency and cross-border practicality more than polished lifestyle branding.",
      },
      {
        name: "Kota Kinabalu",
        note: "Worth exploring if you want a slower coastal setting and are comfortable moving farther from the main peninsula hubs.",
      },
      {
        name: "Ipoh",
        note: "A useful lower-intensity option if cost and calmer daily pace matter more than big-city scale.",
      },
    ],
    cta: {
      eyebrow: "See Malaysia through your own priorities",
      title: "Use the assessment to see whether Malaysia is one of your strongest practical fits.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Malaysia against your budget, comfort threshold, and longer-term relocation goals.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  france: {
    slug: "france",
    name: "France",
    code: "FR",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "France matters in the Relocation Clarity model because it often represents a stronger long-term systems base — especially for users prioritizing healthcare, infrastructure, and cultural depth — while demanding more realism on language, bureaucracy, and cost than some Mediterranean peers.",
    heroHighlights: ["Systems depth", "Healthcare strength", "Regional range", "Long-term base"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1768245076546-4c183cc224bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxQYXJpcyUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzYwMDh8MA&ixlib=rb-4.1.0&q=85",
        alt: "Paris neighborhood street lined with buildings and greenery",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1762853734967-3a15a736bc83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxGcmFuY2UlMjBjaXR5JTIwc3RyZWV0JTIwY29hc3RsaW5lfGVufDB8fHx8MTc3NDczNTk5M3ww&ixlib=rb-4.1.0&q=85",
        alt: "Aerial view of a French coastal city and harbor",
        label: "Coastal range",
      },
      {
        url: "https://images.pexels.com/photos/31482955/pexels-photo-31482955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Montmartre street scene in Paris",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/34832679/pexels-photo-34832679.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Waterfront in Saint-Florent, Corsica",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "France stays in the model because it often offers one of the strongest combinations of healthcare confidence, infrastructure, and long-term everyday systems strength for users who want more than a lifestyle-first move.",
      "It also matters because it is not a simple warm-weather European substitute: France can be an excellent long-term base, but the language expectation, administrative weight, and cost profile create a very different decision from Portugal, Spain, or lower-cost alternatives.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Mixed",
        detail: "France can deliver value for the right profile, but it is rarely the low-cost answer once housing and major-city costs enter the picture.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is one of the clearest reasons France remains relevant in higher-systems comparisons.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Long-stay routes are real, though they often come with more formal documentation and process discipline than users first expect.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can help at the edges, but French matters much more here for smooth everyday life and deeper integration.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "France stays relevant partly because transport, connectivity, and systems depth support a serious longer-term move.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "France offers broad range — large cities, smaller regional bases, and coastal zones — but not one single default lifestyle story.",
      },
    ],
    bestFit: [
      "Healthcare and stronger systems confidence",
      "A more serious long-term European base",
      "Culture, rail connectivity, and regional depth",
      "Users who care about daily quality more than lowest-cost entry",
      "A move with stronger institutional predictability",
      "Urban-to-regional flexibility within one country",
    ],
    tradeoffs: [
      "French matters more here than in some neighboring options, especially if you want life to extend beyond international bubbles.",
      "Administrative processes can feel formal, document-heavy, and slower than the lifestyle image suggests.",
      "Housing and cost pressure rise quickly in Paris and many high-demand regions.",
      "France can reward long-term seriousness, but it is less forgiving as a casual or under-planned move.",
      "Regional lifestyle differences are meaningful — Paris, Lyon, Bordeaux, and Mediterranean France solve different problems.",
    ],
    regions: [
      {
        name: "Paris",
        note: "Best if you want maximum connectivity, scale, and institutional access, while accepting the highest cost and pace pressure.",
      },
      {
        name: "Lyon",
        note: "A strong option if you want big-city functionality with a more grounded feel than Paris.",
      },
      {
        name: "Bordeaux",
        note: "Worth exploring for users who want a polished regional-city base with good livability and transport access.",
      },
      {
        name: "Toulouse",
        note: "Useful if you want a lower-intensity urban base with strong infrastructure and less Paris-level pressure.",
      },
      {
        name: "Nice / Mediterranean Coast",
        note: "Most relevant for climate-first users who still want a more established European systems backdrop.",
      },
    ],
    cta: {
      eyebrow: "See France through your own priorities",
      title: "Use the assessment to see whether France fits your long-term decision — not just your ideal image of Europe.",
      description:
        "This preview is the orientation layer only. The deeper value comes from testing France against your budget, language tolerance, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  germany: {
    slug: "germany",
    name: "Germany",
    code: "DE",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Germany matters in the Relocation Clarity model because it often stands out for systems predictability, infrastructure, healthcare, and long-term practicality — while feeling less lifestyle-led and less forgiving on language, climate, and bureaucracy than warmer-country alternatives.",
    heroHighlights: ["Systems strength", "Infrastructure", "Healthcare confidence", "Long-term practicality"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1728306173203-4ef0151f4071?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxHZXJtYW55JTIwY2l0eSUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzU5OTV8MA&ixlib=rb-4.1.0&q=85",
        alt: "German city skyline from a high viewpoint",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1648764156550-802e36a4854f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxCZXJsaW4lMjBzdHJlZXQlMjBza3lsaW5lfGVufDB8fHx8MTc3NDczNjAxMnww&ixlib=rb-4.1.0&q=85",
        alt: "Berlin skyline at sunset",
        label: "Regional skyline",
      },
      {
        url: "https://images.unsplash.com/photo-1663516003203-63867620cfda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxHZXJtYW55JTIwY2l0eSUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzU5OTV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Street with buildings in a German town",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/34789813/pexels-photo-34789813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Berlin TV Tower reflected in modern architecture",
        label: "Systems orientation",
      },
    ],
    whyIncluded: [
      "Germany stays relevant because it can offer one of the strongest long-term operational cases in the model for users prioritizing healthcare, transport, systems reliability, and serious day-to-day infrastructure.",
      "It also matters because it filters for a specific kind of fit: Germany often works best for people willing to accept a less lifestyle-romantic move in exchange for greater predictability, structure, and long-run practicality.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Mixed",
        detail: "Germany can be workable, but your housing choices will matter more here in major cities, and the generally higher-cost baseline limits its appeal as a budget-first option.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is one of Germany’s strongest reasons for staying in the shortlist.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Germany can be a strong candidate for the right profile, though the path is often more structured and document-dependent than casual movers expect.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English helps in many urban and professional settings, but German still matters for smoother integration and bureaucracy.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Infrastructure, transport, and overall system functionality are central to Germany’s decision-support case.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Germany tends to appeal more for stability and systems than for climate-first or slower-rhythm lifestyle goals.",
      },
    ],
    bestFit: [
      "Long-term systems strength and predictability",
      "Healthcare and institutional confidence",
      "Strong infrastructure and transport",
      "Career or professional practicality for the right profile",
      "Users who value stability over a lifestyle-first narrative",
      "A move intended as a serious long-term base rather than a soft landing",
    ],
    tradeoffs: [
      "Germany can feel less naturally appealing for users whose relocation vision centers on warmth, ease, or slower pace.",
      "Bureaucracy is manageable but rarely light; documentation and sequencing matter.",
      "In the major cities, your housing choices will matter more here and can make the practical case harder than the systems case.",
      "German matters more over time, especially if you want deeper social or institutional integration.",
      "The overall texture can feel more formal and less lifestyle-driven than many other shortlist options.",
    ],
    regions: [
      {
        name: "Berlin",
        note: "Best if you want scale, international energy, and a more flexible urban culture within Germany’s systems framework.",
      },
      {
        name: "Munich",
        note: "Relevant for users who want a highly functional base and strong quality markers, while accepting a sharper cost profile.",
      },
      {
        name: "Hamburg",
        note: "A strong option if you want a calmer big-city feel with transport strength and better day-to-day balance.",
      },
      {
        name: "Cologne / Düsseldorf",
        note: "Worth exploring for users who want a major western Germany base without defaulting to Berlin or Munich.",
      },
      {
        name: "Leipzig",
        note: "Useful if you want a lower-cost urban option with more room than the highest-pressure cities.",
      },
    ],
    cta: {
      eyebrow: "See Germany through your own priorities",
      title: "Use the assessment to see whether Germany is a true fit for your practical needs — not just your respect for its systems.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Germany against your climate, budget, language tolerance, and relocation goals.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  netherlands: {
    slug: "netherlands",
    name: "Netherlands",
    code: "NL",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "The Netherlands matters in the Relocation Clarity model because it often combines English usability, transport strength, compact daily convenience, and regional connectivity — while making users confront a less relaxed cost profile and the reality that their housing choices will matter more here than its smooth-operating reputation suggests.",
    heroHighlights: ["English usability", "Transport strength", "Compact living", "Housing choices matter more"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1610396706842-7218801dd733?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxOZXRoZXJsYW5kcyUyMGNpdHklMjBjYW5hbCUyMHN0cmVldHxlbnwwfHx8fDE3NzQ3MzU5OTZ8MA&ixlib=rb-4.1.0&q=85",
        alt: "Amsterdam canal and bridge",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1585232403751-b1f214935604?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxSb3R0ZXJkYW0lMjBza3lsaW5lJTIwc3RyZWV0fGVufDB8fHx8MTc3NDczNjAyNnww&ixlib=rb-4.1.0&q=85",
        alt: "Rotterdam skyline at night",
        label: "Regional skyline",
      },
      {
        url: "https://images.pexels.com/photos/30084802/pexels-photo-30084802.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Amsterdam townhouse and bicycles on street",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/31763634/pexels-photo-31763634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Amsterdam canal at twilight",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "The Netherlands stays relevant because it can deliver a very high-functioning daily setup for users who care about transit, English usability, compact urban life, and easy regional access across Europe.",
      "It also matters because it forces a more direct tradeoff question: for some people, the Netherlands is one of the smoothest operational fits available; for others, cost and the fact that their housing choices will matter more here make it difficult to justify despite the quality of life signal.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Pressured",
        detail: "The Netherlands is rarely a low-cost move, and housing often becomes the deciding constraint early.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare and systems quality support the Netherlands’ case as a serious long-term base.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "The right pathway can make the Netherlands viable, but the move works best for users with a realistic operational fit rather than a vague preference.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "English usability is one of the clearest reasons the Netherlands keeps showing up in shortlist conversations.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Transit, cycling, connectivity, and compact city design make daily life unusually efficient for many users.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "The Netherlands appeals less for climate fantasy and more for efficient urban functionality and everyday ease.",
      },
    ],
    bestFit: [
      "English usability in daily life",
      "Strong transit, cycling, and compact urban living",
      "A highly connected Europe base",
      "Users who value operational smoothness over lower cost",
      "Remote or knowledge-work practicality",
      "Predictable daily systems with less friction",
    ],
    tradeoffs: [
      "Housing pressure can overpower the rest of the country’s practical strengths if your budget is tight.",
      "The Netherlands is efficient, but not cheap — especially once rent and space constraints enter the picture.",
      "Weather and light conditions are a real lifestyle factor for some users.",
      "The country often works best for people comfortable with denser, more compact living rather than expansive or slower environments.",
      "Operational ease is high, but that does not automatically make it the right emotional or financial fit.",
    ],
    regions: [
      {
        name: "Amsterdam",
        note: "Best if you want maximum international density and connectivity, while accepting that your housing choices will matter more here.",
      },
      {
        name: "Rotterdam",
        note: "Worth exploring if you want a more modern urban feel and a slightly different cost-value balance than Amsterdam.",
      },
      {
        name: "Utrecht",
        note: "Often the most balanced option for users who want centrality, livability, and strong transit without Amsterdam’s full intensity.",
      },
      {
        name: "Eindhoven",
        note: "Relevant for users prioritizing technology, design, or a more focused professional base.",
      },
      {
        name: "Leiden / Haarlem",
        note: "Useful if you want smaller-scale Dutch living while staying tied into the country’s stronger urban network.",
      },
    ],
    cta: {
      eyebrow: "See the Netherlands through your own priorities",
      title: "Use the assessment to see whether the Netherlands is truly worth the tradeoff for your situation.",
      description:
        "This preview is the orientation layer only. The deeper value comes from comparing the Netherlands against your housing budget, pace preferences, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  panama: {
    slug: "panama",
    name: "Panama",
    code: "PA",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Panama matters in the Relocation Clarity model because it often combines residency relevance, regional familiarity, and a more internationally legible operating environment — while requiring more realism on climate, regional unevenness, and lifestyle tradeoffs than headline narratives suggest.",
    heroHighlights: ["Residency relevance", "Regional familiarity", "Urban-coastal split", "Dollarized practicality"],
    images: [
      {
        url: "https://images.pexels.com/photos/15591377/pexels-photo-15591377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Panama City skyline over an urban street",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1701664708493-e7a4012bf525?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxQYW5hbWElMjBjb2FzdGxpbmUlMjBjaXR5fGVufDB8fHx8MTc3NDczNjAyNXww&ixlib=rb-4.1.0&q=85",
        alt: "Panama City skyline at sunset over the ocean",
        label: "Coastal range",
      },
      {
        url: "https://images.unsplash.com/photo-1719439723764-03078f8272d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxDYXNjbyUyMFZpZWpvJTIwUGFuYW1hJTIwc3RyZWV0fGVufDB8fHx8MTc3NDczNjAyNHww&ixlib=rb-4.1.0&q=85",
        alt: "Yellow car parked on a Casco Viejo street in Panama",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1645932377263-dbef81c7635d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxDYXNjbyUyMFZpZWpvJTIwUGFuYW1hJTIwc3RyZWV0fGVufDB8fHx8MTc3NDczNjAyNHww&ixlib=rb-4.1.0&q=85",
        alt: "People walking down a Panama neighborhood street",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Panama stays relevant because it often makes sense for users who want a more practical regional move with real long-stay relevance, established expat ecosystems, and a clearer international-business operating feel than some neighboring options.",
      "It also matters because Panama is not one single experience: the capital, mountain towns, beach communities, and island zones create very different answers on cost, pace, convenience, and social fit.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Panama can be workable, but the value case varies widely between Panama City, expat-heavy beach zones, and interior regions.",
      },
      {
        label: "Healthcare",
        signal: "Strong in key hubs",
        detail: "Healthcare access is part of Panama’s relevance, especially for users centered on Panama City and better-connected areas.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Residency relevance is one of the clearest reasons Panama remains in the curated model.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can be easier in international and expat-heavy pockets than in everyday local settings.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "Panama City can feel quite operationally smooth, but infrastructure consistency drops once you widen the map.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Panama stays relevant because it gives users a choice between city practicality, beach living, and cooler inland bases.",
      },
    ],
    bestFit: [
      "Users prioritizing realistic residency relevance",
      "A move that stays regionally familiar or logistically practical",
      "Urban functionality in Panama City or softer inland alternatives",
      "Warmer climate with expat community options",
      "A shortlist where international-business familiarity matters",
      "People who want choice between city, coast, and mountain settings",
    ],
    tradeoffs: [
      "Panama’s practical case is strongest in some regions and much weaker in others — it rewards location discipline.",
      "Heat, humidity, and traffic can materially shape quality of life, especially around Panama City.",
      "Infrastructure and service quality are not evenly distributed nationwide.",
      "English helps in some circles, but Spanish still matters for smoother day-to-day life and longer-term integration.",
      "Some coastal and expat-heavy areas lose their value case faster than the broader Panama narrative suggests.",
    ],
    regions: [
      {
        name: "Panama City",
        note: "Best if you want maximum healthcare access, services, and international practicality in one place.",
      },
      {
        name: "Coronado / Pacific Beaches",
        note: "Useful for users who want coastal living near the capital’s infrastructure rather than fully remote beach life.",
      },
      {
        name: "Boquete",
        note: "One of the first places to explore if you want cooler weather, a softer pace, and a well-known expat footprint.",
      },
      {
        name: "David / Chiriquí",
        note: "Relevant if you want a more local, lower-intensity western Panama base with practical access to services.",
      },
      {
        name: "Bocas del Toro",
        note: "Most relevant for lifestyle-led coastal users who can tolerate more infrastructure tradeoffs and distance.",
      },
    ],
    cta: {
      eyebrow: "See Panama through your own priorities",
      title: "Use the assessment to see whether Panama is a real fit for your move — not just a strong residency headline.",
      description:
        "This preview is only the orientation layer. The deeper value comes from understanding which version of Panama fits you, and how it compares with your alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  greece: {
    slug: "greece",
    name: "Greece",
    code: "GR",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Greece matters in the Relocation Clarity model because it often offers one of the clearest lifestyle-led Europe options: warm climate, coastal living, and relatively accessible costs compared with some Western European peers — with more system and regional tradeoffs than a simple Mediterranean image suggests.",
    heroHighlights: ["Mediterranean lifestyle", "Relative value in Europe", "Residency relevance", "Regional variation"],
    images: [
      {
        url: "https://images.pexels.com/photos/35886026/pexels-photo-35886026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Waterfront promenade in Thessaloniki, Greece",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1773773975062-0281812802dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxHcmVlY2UlMjBjaXR5JTIwc3RyZWV0JTIwY29hc3R8ZW58MHx8fHwxNzc0NzM2MDAwfDA&ixlib=rb-4.1.0&q=85",
        alt: "Archway with flowers leading to the sea in Greece",
        label: "Coastal living",
      },
      {
        url: "https://images.unsplash.com/photo-1628246317828-18bd3bf12593?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxHcmVlY2UlMjBjaXR5JTIwc3RyZWV0JTIwY29hc3R8ZW58MHx8fHwxNzc0NzM2MDAwfDA&ixlib=rb-4.1.0&q=85",
        alt: "White and blue alley in Greece",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1755458303757-0c6770fc19be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxBdGhlbnMlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM2MDE1fDA&ixlib=rb-4.1.0&q=85",
        alt: "Athens neighborhood view with ruins in the distance",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Greece stays relevant because it offers a different kind of European fit than France, Germany, or the Netherlands: it often appeals to users who want lifestyle, climate, and relative value to matter more than maximum systems strength.",
      "It also matters because Greece is not only an island fantasy. Athens, Thessaloniki, Crete, and mainland regions can create very different tradeoffs around cost, healthcare access, convenience, and daily pace.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced to Strong",
        detail: "Greece often looks attractive for users comparing Europe options, though value changes quickly by island, city, and expat demand.",
      },
      {
        label: "Healthcare",
        signal: "Balanced",
        detail: "Healthcare can be workable, but Greece usually asks for more compromise here than the top systems-oriented European countries.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Residency relevance is part of why Greece remains decision-worthy in the Mediterranean shortlist.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can go fairly far in tourism and expat-adjacent contexts, but everyday local life still becomes easier with Greek.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate",
        detail: "Greece can be very livable, but infrastructure consistency is not the same as in the stronger systems-led European options.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Warm weather, sea access, slower rhythm, and Mediterranean day-to-day appeal are central to Greece’s staying power in the model.",
      },
    ],
    bestFit: [
      "Mediterranean lifestyle and warmer climate",
      "Relative cost relief within Europe",
      "Coastal or island-oriented living",
      "A slower pace of life than higher-pressure northern systems countries",
      "Users comfortable trading some systems strength for lifestyle fit",
      "A shortlist where climate and emotional appeal matter meaningfully",
    ],
    tradeoffs: [
      "Greece usually asks for more compromise on healthcare depth and overall systems consistency than the strongest European alternatives.",
      "Administrative processes can feel slow, uneven, or more relationship-driven than newcomers expect.",
      "Island, mainland, and city living can create very different practicality outcomes.",
      "English can help a lot in some areas, but Greek still matters once life moves beyond visitor or expat-heavy settings.",
      "For some users, the local job market and overall economic texture will feel more limiting than the lifestyle appeal suggests.",
    ],
    regions: [
      {
        name: "Athens",
        note: "Best if you want the strongest service access, international connectivity, and a more practical year-round base.",
      },
      {
        name: "Thessaloniki",
        note: "Worth exploring if you want a lower-intensity urban base with waterfront living and a less pressured feel than Athens.",
      },
      {
        name: "Crete",
        note: "Relevant for users wanting a more substantial island base with more year-round functionality than smaller islands.",
      },
      {
        name: "Peloponnese",
        note: "Useful if you want warmer slower living on the mainland with more room and less island dependency.",
      },
      {
        name: "Cyclades / Island Hubs",
        note: "Most relevant for lifestyle-led users who can tolerate sharper seasonality, logistics, and cost variation.",
      },
    ],
    cta: {
      eyebrow: "See Greece through your own priorities",
      title: "Use the assessment to see whether Greece is a real fit for your priorities — not just a strong Mediterranean idea.",
      description:
        "This preview is only the orientation layer. The deeper value comes from testing Greece against your healthcare expectations, budget, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  canada: {
    slug: "canada",
    name: "Canada",
    code: "CA",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Canada matters in the Relocation Clarity model because it often represents an English-speaking, systems-led, long-term base with strong healthcare and institutional familiarity — while asking for real realism on cost, climate, and the fact that your housing choices will matter more here.",
    heroHighlights: ["English-speaking ease", "Systems confidence", "Long-term stability", "Housing pressure"],
    images: [
      {
        url: "https://images.pexels.com/photos/14323981/pexels-photo-14323981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Toronto skyline across the waterfront",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/18802085/pexels-photo-18802085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Vancouver waterfront skyline and marina",
        label: "Waterfront living",
      },
      {
        url: "https://images.unsplash.com/photo-1751207365282-fa4f2aaf0f88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxDYW5hZGElMjByZXNpZGVudGlhbCUyMHN0cmVldCUyMGNpdHl8ZW58MHx8fHwxNzc0NzM3MTQ3fDA&ixlib=rb-4.1.0&q=85",
        alt: "Canadian city street with flags overhead",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1768301346584-86e781872b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxDYW5hZGElMjByZXNpZGVudGlhbCUyMHN0cmVldCUyMGNpdHl8ZW58MHx8fHwxNzc0NzM3MTQ3fDA&ixlib=rb-4.1.0&q=85",
        alt: "Canadian neighborhood with distant skyline and mountains",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Canada stays relevant because it offers one of the clearest English-speaking, systems-forward long-term relocation cases for users who prioritize healthcare, institutional stability, and everyday familiarity over a purely lifestyle-led move.",
      "It also matters because its appeal is not universal: Canada can be a strong long-run base, but cost, climate, and the fact that your housing choices will matter more here quickly determine whether the practical case is strong enough for a given user.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Pressured",
        detail: "Canada’s systems case can be strong, but housing and cost pressure often narrow its appeal quickly in the main urban markets.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is one of Canada’s clearest reasons for staying in the shortlist for long-term planners.",
      },
      {
        label: "Residency Accessibility",
        signal: "Profile-dependent",
        detail: "Canada can be highly relevant for the right user profile, but the move tends to reward realism and qualification fit rather than casual interest.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "Language ease is part of Canada’s appeal, especially for users comparing it against stronger adaptation-burden countries.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Canada stays relevant partly because its major cities offer the infrastructure and service reliability expected of a serious long-term base.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Lifestyle fit here depends heavily on how much weight you place on stability and familiarity relative to weather and housing costs.",
      },
    ],
    bestFit: [
      "English-speaking ease and lower adaptation burden",
      "Healthcare confidence and institutional stability",
      "A serious long-term base rather than a soft lifestyle move",
      "Users who value predictability over lower cost",
      "Urban multicultural environments with strong services",
      "A move where familiarity and systems matter more than climate appeal",
    ],
    tradeoffs: [
      "Housing pressure in top cities can weaken Canada’s practical case faster than many users expect.",
      "Canada is often more compelling for long-run stability than for short-term value.",
      "Climate becomes a major quality-of-life variable depending on your tolerance for winter and seasonal intensity.",
      "The strongest urban regions are also usually the most expensive and competitive.",
      "For some users, the country feels operationally strong but not emotionally compelling enough to justify the cost tradeoff.",
    ],
    regions: [
      {
        name: "Toronto",
        note: "Best if you want the largest job market and maximum urban infrastructure, while accepting that your housing choices will matter more here.",
      },
      {
        name: "Vancouver",
        note: "Most relevant for users prioritizing climate and nature access, though its cost profile is one of the hardest to justify.",
      },
      {
        name: "Montreal",
        note: "Worth exploring if you want a stronger cultural identity and a different cost-value balance than Toronto or Vancouver.",
      },
      {
        name: "Calgary",
        note: "Useful if you want a more pragmatic city base with comparatively better housing value and strong everyday systems.",
      },
      {
        name: "Halifax",
        note: "Relevant for users who want a smaller coastal city feel and can accept less scale in exchange for lower intensity.",
      },
    ],
    cta: {
      eyebrow: "See Canada through your own priorities",
      title: "Use the assessment to see whether Canada is worth the cost and climate tradeoff for your situation.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Canada against your budget, how much your housing choices will matter here, and its strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  "new-zealand": {
    slug: "new-zealand",
    name: "New Zealand",
    code: "NZ",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "New Zealand matters in the Relocation Clarity model because it often appeals to users looking for an English-speaking, lower-intensity, outdoors-oriented base with strong social trust — while demanding realism on distance, scale, housing, and smaller-market limitations.",
    heroHighlights: ["English-speaking ease", "Lower-intensity pace", "Outdoors orientation", "Distance tradeoff"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1770882202242-6dcc0bf109e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxBdWNrbGFuZCUyMHNreWxpbmUlMjB3YXRlcmZyb250fGVufDB8fHx8MTc3NDczNzE0Nnww&ixlib=rb-4.1.0&q=85",
        alt: "Auckland skyline and waterfront",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1720741349855-a5362834f576?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxRdWVlbnN0b3duJTIwc3RyZWV0JTIwTmV3JTIwWmVhbGFuZHxlbnwwfHx8fDE3NzQ3MzcxMzR8MA&ixlib=rb-4.1.0&q=85",
        alt: "Queenstown lakeside dock with mountains",
        label: "Nature access",
      },
      {
        url: "https://images.pexels.com/photos/35364684/pexels-photo-35364684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Historic street in Oamaru, New Zealand",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1774528212601-39ad5d872349?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxRdWVlbnN0b3duJTIwc3RyZWV0JTIwTmV3JTIwWmVhbGFuZHxlbnwwfHx8fDE3NzQ3MzcxMzR8MA&ixlib=rb-4.1.0&q=85",
        alt: "Town street in New Zealand with hillside homes",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "New Zealand stays relevant because it can offer a highly distinctive fit for users who want social trust, English-speaking ease, and an outdoors-oriented lifestyle without the intensity of larger urban systems countries.",
      "It also matters because it is not a default answer: New Zealand often works best for users who actively value space, pace, and lower-pressure living enough to accept its smaller scale, distance, and housing realities.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Mixed",
        detail: "New Zealand is often less of a value play than its smaller scale suggests, and your housing choices will matter more here.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare and general systems confidence are part of what make New Zealand credible as a long-term base.",
      },
      {
        label: "Residency Accessibility",
        signal: "Profile-dependent",
        detail: "New Zealand can be highly relevant for the right profile, but it tends to be a more serious relocation choice than a casual lifestyle experiment.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "English-speaking ease lowers everyday friction and is one of New Zealand’s clearest comparative advantages.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Daily life can feel smooth and reliable, though the country’s smaller scale shapes what is available and where.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "New Zealand is especially relevant for users who want a calmer, outdoors-led quality of life more than constant urban intensity.",
      },
    ],
    bestFit: [
      "English-speaking ease with lower social friction",
      "Outdoors-oriented or lower-intensity daily living",
      "Users who value trust, pace, and stability over maximum scale",
      "A long-term base where quality of life matters more than urban density",
      "People comfortable with geographic distance from major world hubs",
      "A move where calm and liveability matter more than aggressive cost efficiency",
    ],
    tradeoffs: [
      "Distance from other major regions becomes a real emotional and logistical factor for many users.",
      "Housing costs can feel disproportionate to the country’s size and pace.",
      "New Zealand’s smaller market changes job, service, and opportunity depth compared with larger English-speaking countries.",
      "The country can feel ideal for some users and too quiet or too far for others — the fit is unusually personal.",
      "Regional choice matters: Auckland, Wellington, and South Island living solve meaningfully different lifestyle problems.",
    ],
    regions: [
      {
        name: "Auckland",
        note: "Best if you want the broadest services, international access, and everyday practicality in one place.",
      },
      {
        name: "Wellington",
        note: "Worth exploring if you want a smaller capital-city feel with culture and walkability rather than Auckland scale.",
      },
      {
        name: "Christchurch",
        note: "Useful for users looking for a more manageable urban base with easier access to South Island life.",
      },
      {
        name: "Queenstown",
        note: "Most relevant for lifestyle-led users who strongly prioritize scenery and outdoor access over cost discipline.",
      },
      {
        name: "Tauranga / Bay of Plenty",
        note: "A good orientation point if you want a softer coastal pace without fully giving up practical livability.",
      },
    ],
    cta: {
      eyebrow: "See New Zealand through your own priorities",
      title: "Use the assessment to see whether New Zealand is a real fit for your pace, budget, and long-term goals.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing New Zealand against your distance tolerance, cost threshold, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  australia: {
    slug: "australia",
    name: "Australia",
    code: "AU",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Australia matters in the Relocation Clarity model because it often combines English-speaking ease, strong urban infrastructure, and highly livable coastal-city appeal — while asking for serious realism on cost, distance, and entry practicality.",
    heroHighlights: ["English-speaking ease", "Urban infrastructure", "Coastal-city appeal", "High cost threshold"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1719451271224-a91c9c755547?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxTeWRuZXklMjBoYXJib3VyJTIwc2t5bGluZXxlbnwwfHx8fDE3NzQ3MzcxNDV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Sydney harbour skyline",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/33364419/pexels-photo-33364419.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Sydney waterfront with modern buildings",
        label: "Coastal living",
      },
      {
        url: "https://images.unsplash.com/photo-1721921265766-edaf7d0d4dfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxNZWxib3VybmUlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM3MTQ1fDA&ixlib=rb-4.1.0&q=85",
        alt: "Melbourne street with trams and tall buildings",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1758382850717-8dcb01bd1fc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxNZWxib3VybmUlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM3MTQ1fDA&ixlib=rb-4.1.0&q=85",
        alt: "Australian neighborhood street with a cyclist",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Australia stays relevant because it often offers one of the strongest English-speaking quality-of-life propositions for users who want serious infrastructure, strong healthcare, and coastal-city living in the same conversation.",
      "It also matters because it is a high-threshold option: Australia can be an excellent fit for the right profile, but cost, housing, distance, and practical entry friction make it a much more selective choice than its lifestyle image suggests.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Pressured",
        detail: "Australia’s urban centers often deliver strong quality markers, but housing and cost pressure are usually central decision constraints.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is part of what makes Australia a serious long-term contender for the right user.",
      },
      {
        label: "Residency Accessibility",
        signal: "Profile-dependent",
        detail: "Australia can be highly relevant, but typically for users with a realistic pathway fit rather than a vague lifestyle interest.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "Language ease lowers friction significantly and is one of Australia’s clearest practical advantages.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Australia remains relevant partly because its major cities offer the infrastructure expected of a serious long-term move.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Climate, coastline, and outdoor culture are meaningful parts of Australia’s appeal — but not enough on their own to justify the tradeoffs for every user.",
      },
    ],
    bestFit: [
      "English-speaking ease and lower adaptation burden",
      "Strong urban infrastructure with coastal-city appeal",
      "Users prioritizing quality of life over lower cost",
      "A serious long-term move with stronger systems confidence",
      "People who want outdoor access without giving up major-city functionality",
      "Profiles comfortable with distance in exchange for lifestyle and stability",
    ],
    tradeoffs: [
      "Housing pressure and overall cost are often the first reasons Australia drops out of contention.",
      "Distance matters more than people expect, both emotionally and logistically.",
      "The strongest cities are highly livable but usually come with the sharpest price pressure.",
      "Australia often works best for profiles with a real practical pathway, not just broad preference.",
      "Regional variation matters: Sydney, Melbourne, Brisbane, Perth, and Adelaide are not interchangeable versions of the same move.",
    ],
    regions: [
      {
        name: "Sydney",
        note: "Best if you want maximum global-city infrastructure and coastal appeal, while accepting the highest cost pressure.",
      },
      {
        name: "Melbourne",
        note: "Worth exploring if you want a strong urban base with culture and livability, but less climate-driven branding than Sydney.",
      },
      {
        name: "Brisbane",
        note: "Useful for users who want a warmer large-city option with easier lifestyle access and slightly less pressure than Sydney or Melbourne.",
      },
      {
        name: "Perth",
        note: "Relevant if you want more space and a different pace, while accepting greater distance from the country’s eastern core.",
      },
      {
        name: "Adelaide",
        note: "A good orientation point for users who want a lower-intensity city base without losing everyday infrastructure.",
      },
    ],
    cta: {
      eyebrow: "See Australia through your own priorities",
      title: "Use the assessment to see whether Australia is truly worth the cost and distance tradeoff for you.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Australia against your budget, pathway reality, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  italy: {
    slug: "italy",
    name: "Italy",
    code: "IT",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Italy matters in the Relocation Clarity model because it often represents a lifestyle-led Europe option with cultural depth, regional variety, and meaningful appeal beyond the pure systems-first shortlist — while requiring more realism on administration, regional unevenness, and long-term practical fit.",
    heroHighlights: ["Cultural depth", "Lifestyle-led Europe", "Regional variety", "Administrative tradeoff"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1668494875174-6f1a6b1f4ddc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxJdGFseSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzcxMDR8MA&ixlib=rb-4.1.0&q=85",
        alt: "Italian city hillside buildings",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/19143315/pexels-photo-19143315.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Italian coastal town and blue sea",
        label: "Coastal living",
      },
      {
        url: "https://images.unsplash.com/photo-1689363047129-7b6892add7ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxSb21lJTIwc3RyZWV0JTIwbmVpZ2hib3Job29kfGVufDB8fHx8MTc3NDczNzEyMnww&ixlib=rb-4.1.0&q=85",
        alt: "Rome neighborhood street with historic buildings",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1532340745388-4ca17cd82756?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxJdGFseSUyMGNvYXN0YWwlMjB0b3dufGVufDB8fHx8MTc3NDczNzEzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Italian coastal town at sunset",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Italy stays relevant because it offers a version of Europe that often feels more lifestyle-rich and culturally rewarding than the stronger systems-first options — which matters for users whose relocation decision is not purely administrative.",
      "It also matters because Italy is not one single fit story: northern cities, central cultural hubs, southern regions, and coastal areas can create very different answers on cost, pace, services, and daily convenience.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Italy can offer more value than some premium European peers, but major-city and high-demand regional costs still matter.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare remains part of Italy’s relevance, though practical confidence can vary more by region than in the tightest systems-led countries.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Italy has real long-stay relevance, but it rewards patience and administrative realism more than simple administration.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can carry some situations, but Italian matters more than many new arrivals expect for deeper everyday functioning.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "Infrastructure can be very workable in stronger regions and cities, but national consistency is not the same as in the most systems-led peers.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Italy remains relevant because culture, food, daily street life, and regional character create a strong lifestyle pull for the right user.",
      },
    ],
    bestFit: [
      "Users who value cultural depth and daily atmosphere",
      "A lifestyle-led Europe move rather than a purely systems-led one",
      "Warmer regions and strong food / street-life orientation",
      "People comfortable with more regional nuance and some administrative friction",
      "A move where emotional appeal matters alongside practical fit",
      "Users willing to trade some uniformity for richness of place",
    ],
    tradeoffs: [
      "Administrative processes can feel slower or less linear than the lifestyle appeal suggests.",
      "Regional differences matter more than many users expect — Italy does not deliver one uniform practical experience.",
      "Italian becomes increasingly important once you move beyond visitor-friendly settings.",
      "Some regions feel highly compelling emotionally but less operationally smooth day to day.",
      "Italy can work beautifully for the right user, but it usually rewards users who can tolerate some friction in exchange for quality of life.",
    ],
    regions: [
      {
        name: "Rome",
        note: "Best if you want institutional access, history, and major-city scale in the same place, while accepting more friction.",
      },
      {
        name: "Milan",
        note: "Most relevant for users prioritizing economic gravity, structure, and stronger northern-city practicality.",
      },
      {
        name: "Bologna",
        note: "A strong orientation point if you want a more balanced city base with livability and less tourist distortion.",
      },
      {
        name: "Puglia",
        note: "Useful for users prioritizing slower pace and southern lifestyle appeal more than maximum systems convenience.",
      },
      {
        name: "Sicily",
        note: "Worth exploring if climate and cultural intensity matter strongly and you can tolerate more practical unevenness.",
      },
    ],
    cta: {
      eyebrow: "See Italy through your own priorities",
      title: "Use the assessment to see whether Italy is a real fit for your life — not just a strong emotional draw.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Italy against your administrative tolerance, budget, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  uruguay: {
    slug: "uruguay",
    name: "Uruguay",
    code: "UY",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Uruguay matters in the Relocation Clarity model because it often offers one of the steadier, lower-drama Latin American options for users who prioritize predictability, coastal urban living, and a calmer everyday pace — while asking for realism on scale, cost, and economic depth.",
    heroHighlights: ["Regional steadiness", "Calmer pace", "Coastal urban living", "Smaller-scale tradeoff"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1766792235384-64e8a98cbe2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxVcnVndWF5JTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczNzEwNnww&ixlib=rb-4.1.0&q=85",
        alt: "Uruguayan coastal road with modern buildings",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1766791787805-3a801fe89df7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxVcnVndWF5JTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczNzEwNnww&ixlib=rb-4.1.0&q=85",
        alt: "Uruguayan skyline with beach and cloudy sky",
        label: "Coastal living",
      },
      {
        url: "https://images.pexels.com/photos/36268764/pexels-photo-36268764.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Montevideo street with harbor in the background",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1766791520649-fc34b5cec138?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxNb250ZXZpZGVvJTIwd2F0ZXJmcm9udCUyMHN0cmVldHxlbnwwfHx8fDE3NzQ3MzcxNDh8MA&ixlib=rb-4.1.0&q=85",
        alt: "Montevideo street leading toward the ocean",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Uruguay stays relevant because it can offer a steadier-feeling Latin American base for users who want lower drama, stronger predictability, and a calmer social texture than some larger or more volatile regional options.",
      "It also matters because Uruguay is not automatically the value pick people imagine: the country can work very well for the right user, but the smaller scale and modest economic depth change the decision substantially.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Uruguay can be workable, but it is often not as cheap as people assume when they first compare Latin American options.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare and overall steadiness help keep Uruguay relevant as a calmer regional option.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Uruguay stays in the model partly because it can be a realistic long-stay candidate for users seeking lower-friction regional living.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English is not a major everyday advantage here, so Spanish becomes more important for real integration.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "Daily life can feel stable and manageable, though the country’s smaller scale shapes both opportunity and convenience.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Uruguay often appeals less as a tropical fantasy and more as a steady, coastal, lower-intensity place to actually live.",
      },
    ],
    bestFit: [
      "Users prioritizing steadiness over intensity",
      "A calmer Latin American base with coastal city appeal",
      "People who value lower-drama daily life more than maximum scale",
      "Remote or externally funded lifestyles rather than aggressive local-market ambition",
      "A shortlist where predictability matters more than pure cost",
      "Users looking for a softer landing into Latin America",
    ],
    tradeoffs: [
      "Uruguay’s smaller scale changes the job, service, and opportunity picture more than newcomers often expect.",
      "The country is often steadier than regional peers, but not always cheaper enough to make that tradeoff automatic.",
      "Spanish matters more here because English does not carry everyday life in the way it does in some other previews.",
      "For some users, the slower pace feels grounding; for others, it feels too quiet or too narrow.",
      "Montevideo, Punta del Este, and smaller coastal communities create meaningfully different cost and lifestyle answers.",
    ],
    regions: [
      {
        name: "Montevideo",
        note: "Best if you want the strongest services, healthcare, and year-round practicality in one coastal urban base.",
      },
      {
        name: "Punta del Este",
        note: "Most relevant for users drawn to a polished coastal environment, though it can be less compelling on value.",
      },
      {
        name: "Colonia del Sacramento",
        note: "Worth exploring if you want a quieter historical setting rather than a major-city pace.",
      },
      {
        name: "Piriápolis",
        note: "A useful orientation point for users who want lower-intensity coastal life without Punta del Este’s full pricing feel.",
      },
      {
        name: "Ciudad de la Costa",
        note: "Relevant if you want access to Montevideo while leaning more toward residential coastal living.",
      },
    ],
    cta: {
      eyebrow: "See Uruguay through your own priorities",
      title: "Use the assessment to see whether Uruguay is the right kind of calm for your move.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Uruguay against your budget, language expectations, and strongest regional alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  ecuador: {
    slug: "ecuador",
    name: "Ecuador",
    code: "EC",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Ecuador matters in the Relocation Clarity model because it can offer one of the stronger value-oriented Latin American options for users prioritizing cost relief, climate choice, and practical long-stay relevance — while requiring more realism on infrastructure and regional unevenness.",
    heroHighlights: ["Value orientation", "Climate choice", "Residency relevance", "Regional variation"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1658874286181-0ddefe0b2cd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxFY3VhZG9yJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzMnww&ixlib=rb-4.1.0&q=85",
        alt: "Street in Ecuador with mountain backdrop",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1658874286769-ac34bec90fa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxFY3VhZG9yJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzMnww&ixlib=rb-4.1.0&q=85",
        alt: "Ecuadorian city street with hills beyond",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/35635520/pexels-photo-35635520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Street overlooking a coastal town and sea",
        label: "Coastal range",
      },
      {
        url: "https://images.unsplash.com/photo-1592240819995-1f15496c97ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxFY3VhZG9yJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzMnww&ixlib=rb-4.1.0&q=85",
        alt: "Ecuador neighborhood street with parked cars",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Ecuador stays relevant because it can offer a meaningful value case for users who want lower costs, climate flexibility, and realistic long-stay potential without requiring the same budget as many neighboring or European alternatives.",
      "It also matters because the fit is highly regional: Quito, Cuenca, the coast, and lower-elevation areas can produce very different answers on pace, weather, healthcare access, and daily convenience.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Ecuador remains relevant partly because it can offer meaningful cost relief compared with many shortlist peers.",
      },
      {
        label: "Healthcare",
        signal: "Balanced",
        detail: "Healthcare can be workable in stronger urban bases, but confidence is more region-dependent than in the strongest systems countries.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Long-stay relevance is one of Ecuador’s core reasons for remaining in the model.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can help in some expat-heavy areas, but Spanish quickly becomes important for smoother life on the ground.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate",
        detail: "Some cities and neighborhoods work much better than others, so location choice shapes the daily experience heavily.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Ecuador’s altitude and regional spread create more day-to-day climate choice than many users realize at first.",
      },
    ],
    bestFit: [
      "Users prioritizing lower living costs",
      "Climate flexibility across regions and elevations",
      "A realistic Latin American long-stay option",
      "People comfortable with a more regional, less uniform country fit",
      "A move where value matters more than perfect systems consistency",
      "Users willing to use city choice to shape the experience",
    ],
    tradeoffs: [
      "Infrastructure and healthcare quality vary more by city and neighborhood than many users expect.",
      "Spanish becomes important quickly once you move beyond expat-oriented settings.",
      "The country’s value case is strongest when expectations stay realistic about systems depth and predictability.",
      "Regional choice matters significantly: highland, coastal, and smaller-city living can feel very different.",
      "Ecuador often works best for users who care more about affordability and practical livability than maximum polish.",
    ],
    regions: [
      {
        name: "Quito",
        note: "Best if you want a larger Andean city base with stronger services and administrative practicality.",
      },
      {
        name: "Cuenca",
        note: "Often the first place to explore for users seeking lower-intensity urban living and a well-known expat footprint.",
      },
      {
        name: "Manta",
        note: "Useful if coastal living matters more than Andean climate or big-city depth.",
      },
      {
        name: "Salinas",
        note: "Relevant for users who want an established beach-oriented expat environment with simpler day-to-day rhythms.",
      },
      {
        name: "Loja",
        note: "Worth exploring if you want a smaller, calmer inland base with lower intensity than Quito or Cuenca.",
      },
    ],
    cta: {
      eyebrow: "See Ecuador through your own priorities",
      title: "Use the assessment to see whether Ecuador’s value case is truly strong for your situation.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Ecuador against your infrastructure expectations, climate preferences, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  colombia: {
    slug: "colombia",
    name: "Colombia",
    code: "CO",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Colombia matters in the Relocation Clarity model because it can offer a strong city-choice and climate-choice story for users prioritizing value, urban energy, and regional flexibility — while requiring more realism on safety, consistency, and local adaptation than the headline appeal suggests.",
    heroHighlights: ["City choice", "Climate choice", "Urban energy", "Regional unevenness"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1705966555845-ecbd8cc16c8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxDb2xvbWJpYSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzMzN8MA&ixlib=rb-4.1.0&q=85",
        alt: "Colombian city street with tall buildings",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/11642158/pexels-photo-11642158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Coastal road with ocean view in Colombia",
        label: "Coastal range",
      },
      {
        url: "https://images.unsplash.com/photo-1706151800772-9f5bb5a20f22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxDb2xvbWJpYSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzMzN8MA&ixlib=rb-4.1.0&q=85",
        alt: "Colorful street scene in Colombia",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1764815708450-b5bbbed251cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxDb2xvbWJpYSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzMzN8MA&ixlib=rb-4.1.0&q=85",
        alt: "Steep colorful street in Colombia",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Colombia stays relevant because it offers unusual internal range for users who care about climate choice, different city personalities, and a lower-cost urban base without defaulting immediately to one single model of life abroad.",
      "It also matters because the tradeoffs are real: Colombia can work very well in the right places, but safety, infrastructure consistency, and city-level variation matter more here than in more uniform shortlist countries.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Colombia often remains compelling for users looking for urban energy and practical value at the same time.",
      },
      {
        label: "Healthcare",
        signal: "Balanced to Strong",
        detail: "Healthcare can be strong in the better urban hubs, but confidence is more city-dependent than in the top systems-led countries.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Colombia stays in the model partly because it can be a realistic long-stay candidate for many users exploring Latin America.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English is not a strong everyday advantage, so Spanish meaningfully affects how well the move works.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "Some cities perform much better than others on daily convenience, connectivity, and ease of living.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Colombia’s internal range is a core strength: users can compare cooler, warmer, urban, and coastal profiles within one country.",
      },
    ],
    bestFit: [
      "Users prioritizing climate and city choice",
      "Urban energy at a more workable cost",
      "People who want a lower-cost Latin American city base",
      "A move where internal regional choice is part of the strategy",
      "Users comfortable evaluating cities rather than idealizing the whole country",
      "People who can tolerate more local adaptation in exchange for value and range",
    ],
    tradeoffs: [
      "Safety and day-to-day predictability vary enough by city and neighborhood that broad assumptions are risky.",
      "Spanish matters quickly if you want life to function smoothly outside international pockets.",
      "The country’s strongest value case depends heavily on choosing the right city for your priorities.",
      "Infrastructure and practical convenience can feel very different across urban centers and regional settings.",
      "Colombia often works best for users who are willing to evaluate place-by-place rather than rely on a single country narrative.",
    ],
    regions: [
      {
        name: "Medellín",
        note: "Best if you want climate, urban livability, and strong expat familiarity in the same conversation.",
      },
      {
        name: "Bogotá",
        note: "Relevant for users who want the most institutional and professional gravity, while accepting a denser and cooler city environment.",
      },
      {
        name: "Cali",
        note: "Useful if you want a warmer large-city option with a different cultural texture and less globalized feel.",
      },
      {
        name: "Cartagena",
        note: "Worth exploring if coastal lifestyle matters strongly and you can accept more tourism and pricing distortion.",
      },
      {
        name: "Pereira / Coffee Region",
        note: "A good starting point for users who want a softer city base and a lower-intensity everyday rhythm.",
      },
    ],
    cta: {
      eyebrow: "See Colombia through your own priorities",
      title: "Use the assessment to see whether Colombia’s range is a real advantage for your move.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Colombia’s cities against your safety, climate, and infrastructure priorities.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  vietnam: {
    slug: "vietnam",
    name: "Vietnam",
    code: "VN",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Vietnam matters in the Relocation Clarity model because it can offer one of the strongest affordability-plus-urban-energy propositions in Asia for users comfortable with more local adaptation — while asking for realism on language, bureaucracy, and fit beyond the expat layer.",
    heroHighlights: ["Affordability", "Urban energy", "Regional range", "Adaptation burden"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1702963586674-eb33ab6d6a26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxWaWV0bmFtJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Vietnamese city street with motorbikes",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1728011861438-27e23bc4600f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxWaWV0bmFtJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Quiet street in Vietnam with flags and trees",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1710702418104-6bf5419ab03d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxWaWV0bmFtJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Vietnam road at dusk with motorbikes",
        label: "Regional orientation",
      },
      {
        url: "https://images.unsplash.com/photo-1647592228957-a58967780c95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxWaWV0bmFtJTIwY2l0eSUyMHN0cmVldCUyMGNvYXN0fGVufDB8fHx8MTc3NDczODMzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Vietnam urban neighborhood from above",
        label: "Urban texture",
      },
    ],
    whyIncluded: [
      "Vietnam stays relevant because it can provide one of the strongest cost-to-energy ratios in the model for users who want affordability, city life, and a more dynamic day-to-day environment than some slower lifestyle countries offer.",
      "It also matters because it is not a low-friction answer: Vietnam tends to fit users who can tolerate more language distance, local complexity, and a less Westernized operating environment in exchange for value and momentum.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Strong",
        detail: "Vietnam remains especially relevant for users seeking stronger value without fully giving up city life.",
      },
      {
        label: "Healthcare",
        signal: "Balanced",
        detail: "Healthcare can be workable in the stronger urban hubs, but it is not the country’s main comparative advantage.",
      },
      {
        label: "Residency Accessibility",
        signal: "Mixed",
        detail: "Long-stay practicality exists, but it is often more patchwork and less intuitively stable than some users initially expect.",
      },
      {
        label: "Language / English Ease",
        signal: "Mixed",
        detail: "English can help in some urban or expat-adjacent environments, but daily life still carries a clear language gap.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "The better city bases can be highly workable day to day, especially for users comfortable with a faster street-level environment.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Vietnam’s range between larger cities, coastal locations, and north-south differences makes the fit more nuanced than many expect.",
      },
    ],
    bestFit: [
      "Users prioritizing lower living costs with urban energy",
      "People comfortable with more local adaptation",
      "A move where value and city life matter more than maximum ease",
      "Remote or flexible lifestyles that do not depend on perfect familiarity",
      "Users open to a more dynamic and less polished street-level environment",
      "People who want regional and climate variation inside one country",
    ],
    tradeoffs: [
      "Language distance and local operating texture matter more than many first-time users assume.",
      "Residency and long-stay practicality can feel less straightforward than the cost story suggests.",
      "The country’s main appeal is value and energy, not a uniformly smooth systems experience.",
      "Climate, pace, and urban feel vary significantly between north, south, and coastal locations.",
      "Vietnam often fits best for users who actively prefer a more immersive and adaptive move.",
    ],
    regions: [
      {
        name: "Ho Chi Minh City",
        note: "Best if you want maximum urban energy, business gravity, and day-to-day momentum.",
      },
      {
        name: "Hanoi",
        note: "Relevant for users who want a more traditional and politically central urban base with a different cultural texture.",
      },
      {
        name: "Da Nang",
        note: "Often the first place to explore if you want a calmer coastal city balance between livability and value.",
      },
      {
        name: "Hoi An",
        note: "Useful for lifestyle-led users who can accept a smaller scale and more visitor-oriented environment.",
      },
      {
        name: "Nha Trang",
        note: "Worth exploring if seaside living matters strongly and you are comfortable with a more tourism-shaped context.",
      },
    ],
    cta: {
      eyebrow: "See Vietnam through your own priorities",
      title: "Use the assessment to see whether Vietnam’s value and energy fit your real relocation style.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Vietnam against your adaptation tolerance, residency needs, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  japan: {
    slug: "japan",
    name: "Japan",
    code: "JP",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Japan matters in the Relocation Clarity model because it can represent an unusually strong systems, safety, and urban-functionality option for users willing to accept greater language and cultural distance — while requiring more realism on integration and cost than its global reputation can blur.",
    heroHighlights: ["Systems precision", "Safety", "Urban functionality", "Language distance"],
    images: [
      {
        url: "https://images.pexels.com/photos/7948604/pexels-photo-7948604.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Japanese city skyline across the bay",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1611125162305-ea7550c59dff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxKYXBhbiUyMGNpdHklMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM4MzM2fDA&ixlib=rb-4.1.0&q=85",
        alt: "Japanese neighborhood street with vans and signage",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/30412413/pexels-photo-30412413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Traditional street architecture in Japan",
        label: "Regional orientation",
      },
      {
        url: "https://images.unsplash.com/photo-1744137304451-063dad2bc871?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxKYXBhbiUyMGNpdHklMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM4MzM2fDA&ixlib=rb-4.1.0&q=85",
        alt: "Historic narrow lane in Japan",
        label: "Local texture",
      },
    ],
    whyIncluded: [
      "Japan stays relevant because it can offer one of the strongest combinations of safety, urban precision, and everyday systems reliability in the model for users who are not primarily optimizing for cultural familiarity.",
      "It also matters because it is not a universal fit: Japan often works best for users who are comfortable with a greater integration burden and who value order, safety, and operational quality enough to justify that tradeoff.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Japan’s value story depends heavily on city choice and expectations; it is rarely the cheapest option, but not always as out of reach as some users assume.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is one part of Japan’s broader systems-led case.",
      },
      {
        label: "Residency Accessibility",
        signal: "Profile-dependent",
        detail: "Japan can be highly relevant for the right profile, though it tends to reward seriousness and pathway clarity more than casual interest.",
      },
      {
        label: "Language / English Ease",
        signal: "Challenging",
        detail: "Language adaptation is one of Japan’s clearest friction points and meaningfully changes the fit.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Infrastructure, transport, and everyday operational reliability are central to Japan’s continued relevance.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Japan tends to appeal more through safety, order, and urban functionality than through a low-friction lifestyle fantasy.",
      },
    ],
    bestFit: [
      "Users prioritizing safety and urban systems quality",
      "People willing to accept stronger language and cultural distance",
      "A move where operational excellence matters more than low adaptation burden",
      "Users who value order, transit, and everyday reliability",
      "A serious long-term base rather than a casual trial move",
      "People comfortable with a more demanding integration curve",
    ],
    tradeoffs: [
      "Language is a major fit variable and often the clearest reason Japan is not the right answer for everyone.",
      "Japan’s strongest appeal is operational, but that does not automatically make it emotionally or socially easy to live in long term.",
      "The country often rewards users who are comfortable with stronger structure and less improvisational everyday life.",
      "Residency practicality tends to depend on profile fit rather than broad accessibility.",
      "Regional and city differences matter, but none fully remove the adaptation burden that defines Japan’s overall fit pattern.",
    ],
    regions: [
      {
        name: "Tokyo",
        note: "Best if you want maximum scale, infrastructure, and professional gravity in one place.",
      },
      {
        name: "Osaka",
        note: "Useful for users who want a major city with a somewhat different social and lifestyle texture than Tokyo.",
      },
      {
        name: "Yokohama",
        note: "Worth exploring if you want strong urban functionality with slightly less intensity than central Tokyo.",
      },
      {
        name: "Fukuoka",
        note: "Often relevant for users seeking a more manageable large-city base without giving up too much connectivity.",
      },
      {
        name: "Kyoto",
        note: "A better orientation point for users drawn to cultural atmosphere more than maximum scale or speed.",
      },
    ],
    cta: {
      eyebrow: "See Japan through your own priorities",
      title: "Use the assessment to see whether Japan’s systems strengths justify the adaptation tradeoff for you.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Japan against your language tolerance, pathway reality, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  singapore: {
    slug: "singapore",
    name: "Singapore",
    code: "SG",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Singapore matters in the Relocation Clarity model because it often represents one of the clearest high-functionality urban bases in the world: English-usable, infrastructure-heavy, safe, and regionally connected — while demanding realism on cost, scale, and entry selectivity.",
    heroHighlights: ["High functionality", "English usability", "Regional hub", "Cost threshold"],
    images: [
      {
        url: "https://images.pexels.com/photos/18340271/pexels-photo-18340271.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Singapore skyline at golden hour",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/15314393/pexels-photo-15314393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Singapore river and city lights at night",
        label: "Waterfront living",
      },
      {
        url: "https://images.unsplash.com/photo-1597506286875-051abb760845?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxTaW5nYXBvcmUlMjBzdHJlZXQlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc0NzM4MzY0fDA&ixlib=rb-4.1.0&q=85",
        alt: "Tree-lined Singapore street with pedestrians",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1760377964819-4e511847698d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxTaW5nYXBvcmUlMjBza3lsaW5lJTIwc3RyZWV0JTIwd2F0ZXJmcm9udHxlbnwwfHx8fDE3NzQ3MzgzNTF8MA&ixlib=rb-4.1.0&q=85",
        alt: "Singapore Flyer and cityscape",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Singapore stays relevant because it can offer an unusually strong package of English usability, urban safety, healthcare, and infrastructure for users who want a highly functional regional Asian base rather than a lower-cost but messier alternative.",
      "It also matters because it forces a very clear tradeoff question: for some users Singapore is almost the ideal operating environment, but for others the cost and compactness make it too selective to justify.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Pressured",
        detail: "Singapore’s practical strengths are clear, but they come with one of the sharpest cost profiles in the entire shortlist.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare confidence is one of Singapore’s strongest reasons for staying in the curated model.",
      },
      {
        label: "Residency Accessibility",
        signal: "Selective",
        detail: "Singapore is highly relevant for the right profile, but not a broad-access option for every relocation path.",
      },
      {
        label: "Language / English Ease",
        signal: "Strong",
        detail: "English usability is one of the clearest practical advantages Singapore holds over many regional peers.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Infrastructure, transport, and daily urban functionality are central to Singapore’s appeal.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Balanced",
        detail: "Singapore tends to appeal more as a highly workable urban base than as a spacious or lower-intensity lifestyle destination.",
      },
    ],
    bestFit: [
      "Users prioritizing maximum urban functionality",
      "English usability in daily life",
      "A strong regional Asia base with lower friction",
      "Healthcare and safety confidence",
      "People comfortable paying for operational smoothness",
      "Users who prefer a compact, high-functioning city model",
    ],
    tradeoffs: [
      "Cost is the first and clearest filter for whether Singapore makes sense at all.",
      "The country’s compact size changes the lifestyle feel for users who want more space or regional variation.",
      "Singapore often works best for users with a strong practical pathway rather than a casual lifestyle preference.",
      "Its strengths are operationally obvious, but that does not mean it is the most emotionally resonant fit for every user.",
      "Singapore is rarely the value option — it is the efficiency option.",
    ],
    regions: [
      {
        name: "Central Core",
        note: "Best if you want maximum transit access, business density, and everyday convenience.",
      },
      {
        name: "East Coast",
        note: "Useful for users who want a more residential rhythm without leaving the city’s practical infrastructure behind.",
      },
      {
        name: "River Valley / Orchard",
        note: "Relevant if premium central-city living and direct access matter more than cost discipline.",
      },
      {
        name: "Tiong Bahru / Mature Neighborhoods",
        note: "A good orientation point for users wanting a more lived-in urban feel rather than a purely corporate texture.",
      },
      {
        name: "West / One-North Corridor",
        note: "Worth exploring if professional practicality and ecosystem access matter more than a lifestyle-forward address.",
      },
    ],
    cta: {
      eyebrow: "See Singapore through your own priorities",
      title: "Use the assessment to see whether Singapore’s functionality is worth the cost threshold for you.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Singapore against your budget, compact-living tolerance, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  croatia: {
    slug: "croatia",
    name: "Croatia",
    code: "HR",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Croatia matters in the Relocation Clarity model because it often appeals as a lighter, coastal Europe option with relative value and lifestyle appeal — while requiring more realism on seasonality, smaller-scale systems, and how different inland and Adriatic living can feel.",
    heroHighlights: ["Adriatic lifestyle", "Relative value in Europe", "Seasonality tradeoff", "Smaller-scale systems"],
    images: [
      {
        url: "https://images.pexels.com/photos/11828532/pexels-photo-11828532.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Dubrovnik coastal roads and city walls",
        label: "Coastal base",
      },
      {
        url: "https://images.unsplash.com/photo-1756569012465-4cc055f6ab26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxEdWJyb3ZuaWslMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzNjV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Historic Dubrovnik square with people walking",
        label: "Urban texture",
      },
      {
        url: "https://images.pexels.com/photos/29988354/pexels-photo-29988354.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Stone buildings in Dubrovnik old town",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1685978380261-25bf17c01625?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxEdWJyb3ZuaWslMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzNjV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Dubrovnik rooftops from above",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Croatia stays relevant because it can offer a more lifestyle-led Adriatic Europe option for users who want sea access, relative value, and a slower pace without moving fully outside the European frame.",
      "It also matters because it is not a one-note coast fantasy: Croatia’s real decision turns on seasonality, scale, and whether a user wants summer appeal, year-round practicality, or something in between.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Croatia can feel attractive compared with some Western European peers, but coastal demand pressure changes the value picture quickly.",
      },
      {
        label: "Healthcare",
        signal: "Balanced",
        detail: "Healthcare can be workable, but it is not the country’s main reason for being in the shortlist.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Croatia remains relevant partly because it can be a viable long-stay option for users prioritizing lifestyle and location.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English can go fairly far in tourism-adjacent and urban settings, but local life still becomes easier with Croatian.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate",
        detail: "The country can be very livable, but users should not expect the same systems density as the strongest northern Europe options.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Adriatic access, smaller-scale cities, and slower seasonal rhythm are central to Croatia’s appeal.",
      },
    ],
    bestFit: [
      "Users prioritizing coastal Europe and sea access",
      "People who value lifestyle appeal over maximum systems strength",
      "A move where relative value inside Europe still matters",
      "Users comfortable with a smaller-scale urban environment",
      "People drawn to slower rhythm and Adriatic living",
      "A shortlist where year-round practicality is not the only decision lens",
    ],
    tradeoffs: [
      "Seasonality materially changes how some regions feel as real long-term bases.",
      "Croatia’s strongest lifestyle appeal does not automatically mean stronger healthcare or systems depth.",
      "Coastal value can erode quickly in the highest-demand areas.",
      "Smaller scale can feel calming for some users and limiting for others.",
      "Croatia often works best for users who know they are prioritizing lifestyle and location over maximum operational density.",
    ],
    regions: [
      {
        name: "Zagreb",
        note: "Best if you want the strongest year-round practicality and services rather than a coast-first base.",
      },
      {
        name: "Split",
        note: "Useful for users who want a clear Adriatic lifestyle option with more city substance than a smaller resort town.",
      },
      {
        name: "Dubrovnik",
        note: "Most relevant for users prioritizing coastal beauty and are comfortable with stronger tourism pressure.",
      },
      {
        name: "Rijeka",
        note: "Worth exploring if you want a less polished but more grounded coastal city base.",
      },
      {
        name: "Istria",
        note: "A strong orientation point for slower lifestyle seekers who want regional charm more than big-city function.",
      },
    ],
    cta: {
      eyebrow: "See Croatia through your own priorities",
      title: "Use the assessment to see whether Croatia is a real long-term fit — not only a strong Adriatic image.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Croatia against your seasonality tolerance, systems expectations, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  argentina: {
    slug: "argentina",
    name: "Argentina",
    code: "AR",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Argentina matters in the Relocation Clarity model because it can offer a compelling mix of urban culture, lifestyle depth, and potential value for users who can tolerate more economic volatility — while requiring unusually high realism on currency and stability tradeoffs.",
    heroHighlights: ["Cultural depth", "Urban lifestyle", "Potential value", "Economic volatility"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1674942002140-ec266539c4ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxCdWVub3MlMjBBaXJlcyUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzgzNjd8MA&ixlib=rb-4.1.0&q=85",
        alt: "Buenos Aires street with colorful building",
        label: "Urban base",
      },
      {
        url: "https://images.unsplash.com/photo-1580614496949-74daa329baef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxCdWVub3MlMjBBaXJlcyUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzgzNjd8MA&ixlib=rb-4.1.0&q=85",
        alt: "Buenos Aires neighborhood street",
        label: "Everyday street view",
      },
      {
        url: "https://images.unsplash.com/photo-1694656822724-7aaeb4511bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxCdWVub3MlMjBBaXJlcyUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzgzNjd8MA&ixlib=rb-4.1.0&q=85",
        alt: "Buenos Aires street with graffiti and mixed architecture",
        label: "Urban texture",
      },
      {
        url: "https://images.unsplash.com/photo-1679417302656-9b5170584526?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxCdWVub3MlMjBBaXJlcyUyMHN0cmVldCUyMG5laWdoYm9yaG9vZHxlbnwwfHx8fDE3NzQ3MzgzNjd8MA&ixlib=rb-4.1.0&q=85",
        alt: "Colorful corner in Buenos Aires",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Argentina stays relevant because it can offer a rare mix of urban cultural richness, strong city life, and at times unusually favorable value for users who can tolerate more macro-level instability than most shortlist countries.",
      "It also matters because it creates a very clear fit question: some users find Argentina’s energy and quality-of-life texture deeply compelling, while others cannot justify the volatility tradeoff even if the everyday life looks attractive.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Potentially Strong",
        detail: "Argentina can appear highly attractive on value, but that story is inseparable from volatility and changing conditions.",
      },
      {
        label: "Healthcare",
        signal: "Balanced to Strong",
        detail: "Healthcare can be workable in the strongest urban bases, though it is not the country’s defining advantage.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Argentina remains relevant partly because it can be a realistic long-stay candidate for users looking beyond the most conventional shortlist countries.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English helps in limited contexts, but Spanish matters substantially for real life and longer-term ease.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Moderate to Strong",
        detail: "The strongest urban environments can be highly livable, but country-level consistency is not the main appeal here.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Strong",
        detail: "Argentina stays relevant because cultural atmosphere, social texture, and city life can feel richer than a simple affordability story suggests.",
      },
    ],
    bestFit: [
      "Users drawn to cultural depth and city life",
      "People who can tolerate more economic uncertainty in exchange for daily-life richness",
      "A move where emotional and cultural quality matter alongside value",
      "Urban users who want a stronger sense of place rather than a generic expat base",
      "People comfortable with Spanish becoming part of the move",
      "Users who see volatility as a tradeoff to evaluate, not an automatic dealbreaker",
    ],
    tradeoffs: [
      "Economic volatility is not a side issue here — it is central to the decision.",
      "Argentina can feel highly rewarding day to day while still being difficult to frame as a fully predictable long-term base.",
      "The strongest urban appeal does not remove the need for realism on currency and practical stability.",
      "Spanish matters more than many new arrivals initially assume for smoother integration.",
      "Argentina often works best for users who know they are accepting volatility in exchange for a certain kind of life quality.",
    ],
    regions: [
      {
        name: "Buenos Aires",
        note: "Best if you want maximum urban culture, scale, and everyday city energy in one place.",
      },
      {
        name: "Mendoza",
        note: "Worth exploring if you want a lower-intensity base with climate appeal and less big-city pressure.",
      },
      {
        name: "Córdoba",
        note: "Useful for users who want a major inland city with practical scale but a different rhythm from Buenos Aires.",
      },
      {
        name: "Rosario",
        note: "Relevant if you want a large city environment without fully defaulting to the capital.",
      },
      {
        name: "Patagonia Gateways",
        note: "A better orientation point for users prioritizing environment and pace over maximum urban infrastructure.",
      },
    ],
    cta: {
      eyebrow: "See Argentina through your own priorities",
      title: "Use the assessment to see whether Argentina’s lifestyle upside is worth the volatility tradeoff for you.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Argentina against your stability needs, budget tolerance, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
  chile: {
    slug: "chile",
    name: "Chile",
    code: "CL",
    subtitle: "A practical overview through the Relocation Clarity lens",
    intro:
      "Chile matters in the Relocation Clarity model because it can offer one of the more structured and urban-functional Latin American options for users prioritizing predictability, city systems, and climate range — while asking for more realism on cost and social texture than some lower-cost neighbors.",
    heroHighlights: ["Structured LATAM option", "Urban functionality", "Climate range", "Cost tradeoff"],
    images: [
      {
        url: "https://images.unsplash.com/photo-1721058459284-9b1ce24c749c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxDaGlsZSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzNTB8MA&ixlib=rb-4.1.0&q=85",
        alt: "Santiago skyline in Chile",
        label: "Urban base",
      },
      {
        url: "https://images.pexels.com/photos/17808457/pexels-photo-17808457.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Valparaíso colorful hillside and sea",
        label: "Coastal range",
      },
      {
        url: "https://images.unsplash.com/photo-1626123007398-56c3bdb7e464?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxDaGlsZSUyMGNpdHklMjBzdHJlZXQlMjBjb2FzdHxlbnwwfHx8fDE3NzQ3MzgzNTB8MA&ixlib=rb-4.1.0&q=85",
        alt: "Chilean city street with pedestrian",
        label: "Everyday street view",
      },
      {
        url: "https://images.pexels.com/photos/12492997/pexels-photo-12492997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Aerial view of Valparaiso at dawn",
        label: "Regional orientation",
      },
    ],
    whyIncluded: [
      "Chile stays relevant because it can offer a more structured-feeling Latin American option for users who want greater urban order, stronger institutional confidence, and a move that may feel less improvisational than some neighboring alternatives.",
      "It also matters because Chile is not simply a safer regional default: cost, regional spread, and the country’s more restrained social texture change the decision meaningfully depending on the person.",
    ],
    snapshotSignals: [
      {
        label: "Affordability",
        signal: "Balanced",
        detail: "Chile can be workable, but it often makes less sense as a pure low-cost play than other Latin American shortlist countries.",
      },
      {
        label: "Healthcare",
        signal: "Strong",
        detail: "Healthcare and institutional confidence help keep Chile relevant as a more structured regional option.",
      },
      {
        label: "Residency Accessibility",
        signal: "Practical",
        detail: "Chile remains decision-worthy partly because it can support a serious long-stay move for users looking for more order and practicality.",
      },
      {
        label: "Language / English Ease",
        signal: "Moderate",
        detail: "English is not the main operating advantage here, so Spanish still shapes the real fit of the move.",
      },
      {
        label: "Infrastructure / Internet",
        signal: "Strong",
        detail: "Chile stays relevant partly because its stronger urban environments can feel more organized and operationally dependable than many users expect.",
      },
      {
        label: "Lifestyle / Climate",
        signal: "Flexible",
        detail: "Chile’s north-south length creates unusually wide climate and place variation for users comparing urban, coastal, and cooler-region options.",
      },
    ],
    bestFit: [
      "Users prioritizing a more structured Latin American option",
      "People who value urban functionality and steadier systems",
      "A move where predictability matters more than lowest-cost entry",
      "Users comfortable using regional choice to shape climate and pace",
      "People who want a serious city base rather than purely lifestyle-forward relocation",
      "A shortlist where institutional confidence matters alongside geography",
    ],
    tradeoffs: [
      "Chile can feel more orderly than some regional peers, but often at a higher cost than users initially expect.",
      "The country’s strengths are practical and structural more than emotionally lush or lifestyle-romantic.",
      "Spanish still matters substantially for smoother local integration and longer-term functioning.",
      "Regional spread is huge, so users need to think more carefully about where in Chile actually fits them.",
      "For some users, Chile feels reassuring; for others, it feels less vivid than other Latin American options with similar or lower costs.",
    ],
    regions: [
      {
        name: "Santiago",
        note: "Best if you want the strongest services, healthcare, and city infrastructure in one place.",
      },
      {
        name: "Valparaíso / Viña del Mar",
        note: "Useful for users who want coastal access and cultural texture without fully giving up urban practicality.",
      },
      {
        name: "La Serena",
        note: "Worth exploring if you want a calmer coastal base with a lower-intensity feel than Santiago.",
      },
      {
        name: "Concepción",
        note: "Relevant if you want a major southern city alternative with a different climate and pace profile.",
      },
      {
        name: "Southern Lakes Region",
        note: "A strong orientation point for users prioritizing scenery and lower-intensity living more than maximum scale.",
      },
    ],
    cta: {
      eyebrow: "See Chile through your own priorities",
      title: "Use the assessment to see whether Chile’s structured regional fit is actually right for you.",
      description:
        "This preview is only the orientation layer. The deeper value comes from comparing Chile against your cost expectations, climate preferences, and strongest alternatives.",
      primaryLabel: "Take the assessment",
      primaryHref: "/auth",
      secondaryLabel: "See a sample Relocation Blueprint",
      secondaryHref: "/sample-plan",
    },
  },
};

export const getCountryPreviewContent = (slug) => countryPreviewContent[slug] || null;

export const getCountryPreviewSlugByCode = (code) => {
  const match = Object.values(countryPreviewContent).find((country) => country.code === code);
  return match?.slug || null;
};